import { animate, style, transition, trigger } from '@angular/animations';
import {
  AfterContentInit,
  Component,
  DOCUMENT,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, NavigationSkipped, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Subscription } from 'rxjs';
import { BurgerMenuContent, CategoryBurgerLink, Link, LinkContent, StoreType, SubNavigationContent } from 'src/app/models';
import { CategoryService } from 'src/app/services/category/category.service';
import { ContentService, sections } from 'src/app/services/content/content.service';
import { KioskService } from 'src/app/services/kiosk/kiosk.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { environment } from 'src/environments/environment';
import { buttonIcons } from '../../../modules/icon.module';
import { TextButtonComponent } from '../../components';
import { ContentSection } from '../content-section';

interface Socials {
  facebook: string;
  instagram: string;
}

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  animations: [
    trigger('installAnimation', [
      transition(':enter', [style({ opacity: 0, width: 0 }), animate('800ms ease-out', style({ opacity: 1, width: '*' }))]),
      transition(':leave', [style({ opacity: 1, width: '*' }), animate('800ms ease-in', style({ opacity: 0, width: 0 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterLink, RouterLinkActive, MatIconModule, TextButtonComponent],
})
export class NavigationComponent extends ContentSection implements OnInit, AfterContentInit, OnDestroy {
  private themeService = inject(ThemeService);
  private kioskService = inject(KioskService);
  private categoryService = inject(CategoryService);
  private elRef = inject(ElementRef);
  private router = inject(Router);
  private document = inject<Document>(DOCUMENT);
  private ngZone = inject(NgZone);

  subscription: Subscription;

  contentUrl = `${environment.urls.content.merchant_content}/`;
  contactUsLink = `/${environment.urls.content.contact_us}`;
  filterIcon = buttonIcons.filter;

  @Input() showFilter: boolean;
  @ViewChild('tooltipRef') tooltipRef: ElementRef;
  @HostBinding('class.hide-border') hideBorder = false;

  hideGroceries: boolean;
  groceries: Link;
  searchPlaceholder = '';
  searchButton = '';

  showTooltip = false;
  showSocials = false;

  links: (LinkContent | SubNavigationContent | BurgerMenuContent | CategoryBurgerLink)[];
  openNavigationName?: string;
  openNavigationRef: HTMLElement;
  closeSubNavTimeout: any;

  socials: Socials;

  @HostListener('focusout', ['$event']) onFocusOut(event: FocusEvent) {
    if (!this.openNavigationRef?.contains(event.relatedTarget as any) && this.document.hasFocus()) {
      this.closeNavigation();
    }
  }

  @HostListener('mouseenter', ['$event']) onMouseEnter(event: MouseEvent) {
    clearTimeout(this.closeSubNavTimeout);
  }

  @HostListener('mouseleave', ['$event']) onMouseLeave(event: MouseEvent) {
    this.ngZone.runOutsideAngular(() => {
      this.closeSubNavTimeout = setTimeout(() => {
        this.closeNavigation();
      }, 2000);
    });
  }

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, sections.header);

    const buttonStyle = this.content.get('groceriesButton');
    this.groceries = { url: environment.urls.products, name: this.content.get('groceries') || 'groceries', button: buttonStyle };
    this.searchPlaceholder = this.content.get('searchPlaceholder') || 'searchPlaceholder';
    this.searchButton = this.content.get('searchButtonText') || 'searchButtonText';
    this.hideGroceries = this.content.get('hideGroceries');

    this.socials = {
      facebook: this.content.get('facebookLink'),
      instagram: this.content.get('instagramLink'),
    };

    // TODO: add accessability

    const merchantContent = this.contentService.getMerchantContent();
    this.links = [];
    for (const entry of merchantContent) {
      switch (entry.type) {
        case 'simple': {
          const link = this.contentService.getContentLink(entry);
          this.links.push(link);
          break;
        }
        case 'categoryBurger': {
          const link = this.categoryService.buildCategoryBurgerMenu(entry);
          if (link) this.links.push(link);
          break;
        }
        case 'link':
        case 'subNav':
        case 'burger':
          this.links.push(entry);
          break;
      }
    }
  }

  ngOnInit() {
    this.subscription = new Subscription();

    const type = this.contentService.getMerchant().type;
    if ((type === StoreType.FANDANKO || type === StoreType.FCKING_PIZZA) && !this.kioskService.isKiosk) {
      this.showSocials = true;
    }

    if (this.router.url === '/') {
      this.hideBorder = true;
    }

    this.subscription.add(
      this.router.events.subscribe((event) => {
        switch (true) {
          case (event instanceof NavigationEnd || event instanceof NavigationSkipped) && event.url === '/':
            this.hideBorder = true;
            break;
          default:
            this.hideBorder = false;
        }
      }),
    );
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  openTooltip() {
    this.showTooltip = true;
    this.tooltipRef.nativeElement.focus();
  }

  openNavigation(el: HTMLElement, navigation: string) {
    if (this.openNavigationRef) {
      this.openNavigationRef.blur();
    }

    this.openNavigationName = navigation;
    this.openNavigationRef = el;

    this.ngZone.runOutsideAngular(() => setTimeout(() => el.focus(), 0));
  }

  closeNavigation() {
    this.openNavigationName = undefined;
  }

  closeTooltip() {
    this.showTooltip = false;
  }

  sameSite(link: LinkContent) {
    return this.contentService.sameSite(link);
  }
}
